import React, { useState } from 'react';
import { Box, Text, HStack } from '@chakra-ui/react';

interface CalendarProps {
  selectedDate: Date | null;
  onDateSelect: (date: Date) => void;
  onClose: () => void;
  validationType?: 'age' | 'future';
}

const Calendar: React.FC<CalendarProps> = ({ selectedDate, onDateSelect, onClose, validationType = 'age' }) => {
  // Initialize with a date that makes the user 20 years old
  const getInitialDate = () => {
    if (selectedDate) return selectedDate;
    const today = new Date();
    
    if (validationType === 'future') {
      // For future dates, start with today
      return today;
    } else {
      // For age validation, start with 20 years ago
      const twentyYearsAgo = new Date(today.getFullYear() - 20, today.getMonth(), today.getDate());
      return twentyYearsAgo;
    }
  };
  
  const [currentDate, setCurrentDate] = useState(getInitialDate());
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };
  
  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    const today = new Date();

    if (validationType === 'future') {
      // Prevent navigating to a month before the current month
      if (direction === 'prev') {
        const targetMonth = newDate.getMonth() - 1;
        const targetYear = targetMonth < 0 ? newDate.getFullYear() - 1 : newDate.getFullYear();
        const adjustedMonth = targetMonth < 0 ? 11 : targetMonth;
        if (targetYear < today.getFullYear() || (targetYear === today.getFullYear() && adjustedMonth < today.getMonth())) {
          return; // Blocked: cannot go before current month
        }
      }
      newDate.setMonth(newDate.getMonth() + (direction === 'prev' ? -1 : 1));
    } else {
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
    }
    setCurrentDate(newDate);
  };

  const navigateYear = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    const currentYear = newDate.getFullYear();
    const today = new Date();

    if (validationType === 'future') {
      // For future dates, only restrict going too far back (no upper limit)
      const minYear = today.getFullYear(); // Cannot go before current year

      if (direction === 'prev') {
        if (currentYear > minYear) {
          newDate.setFullYear(currentYear - 1);
        }
      } else {
        newDate.setFullYear(currentYear + 1);
      }
    } else {
      // For age validation, restrict both directions (18-100 years old)
      const maxYear = today.getFullYear() - 18; // Minimum age 18
      const minYear = today.getFullYear() - 100; // Maximum age 100

      if (direction === 'prev') {
        if (currentYear > minYear) {
          newDate.setFullYear(currentYear - 1);
        }
      } else {
        if (currentYear < maxYear) {
          newDate.setFullYear(currentYear + 1);
        }
      }
    }
    setCurrentDate(newDate);
  };
  
  const handleDateClick = (date: Date) => {
    if (isValidDate(date)) {
      onDateSelect(date);
      onClose();
    }
  };
  
  const isSelectedDate = (date: Date) => {
    if (!selectedDate) return false;
    return date.toDateString() === selectedDate.toDateString();
  };

  const isValidDate = (date: Date) => {
    const today = new Date();
    
    if (validationType === 'future') {
      // For future dates, only allow today and future dates
      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      return date >= todayStart;
    } else {
      // For age validation (original logic)
      const age = today.getFullYear() - date.getFullYear();
      const monthDiff = today.getMonth() - date.getMonth();
      const dayDiff = today.getDate() - date.getDate();
      
      // Calculate exact age
      let exactAge = age;
      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        exactAge = age - 1;
      }
      
      return exactAge >= 18 && exactAge <= 100;
    }
  };
  
  const days = getDaysInMonth(currentDate);
  
  return (
    <Box
      position="relative"
      width="343px"
      height="336px"            
      backgroundColor="#120F1B"
      borderRadius="13px"
      border="1px solid"
      borderColor="rgba(29, 27, 38, 1)"
      boxShadow="0px 10px 60px 0px rgba(0, 0, 0, 0.1), 0px 4px 4px 0px rgba(0, 0, 0, 0.25)"
      padding="8px 16px"
      data-calendar="true"
    >
      {/* Month Picker */}
      <HStack justifyContent="space-between" alignItems="center" marginBottom="12px" width="311px">
        <HStack alignItems="center" spacing="8px">
          <Text
            fontFamily="Poppins, sans-serif"
            fontWeight="400"
            fontSize="20px"
            lineHeight="1em"
            color="#C6D959"
            textAlign="center"
          >
            {monthNames[currentDate.getMonth()]}
          </Text>
          <HStack alignItems="center" spacing="4px">
            <Box
              width="24px"
              height="24px"
              cursor="pointer"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigateYear('prev');
              }}
              display="flex"
              alignItems="center"
              justifyContent="center"
              _hover={{
                opacity: 0.7
              }}
            >
              <svg width="16" height="16" viewBox="0 0 8 8" fill="none">
                <path d="M5 1L2 4L5 7" stroke="#C6D959" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Box>
            <Text
              fontFamily="Poppins, sans-serif"
              fontWeight="400"
              fontSize="20px"
              lineHeight="1em"
              color="#C6D959"
              textAlign="center"
            >
              {currentDate.getFullYear()}
            </Text>
            <Box
              width="24px"
              height="24px"
              cursor="pointer"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigateYear('next');
              }}
              display="flex"
              alignItems="center"
              justifyContent="center"
              _hover={{
                opacity: 0.7
              }}
            >
              <svg width="16" height="16" viewBox="0 0 8 8" fill="none">
                <path d="M3 1L6 4L3 7" stroke="#C6D959" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Box>
          </HStack>
          <Box width="6.69px" height="11.44px">
            {/* Chevron icon placeholder */}
          </Box>
        </HStack>
        
        <HStack spacing="31px" width="51px" justifyContent="space-between">
          <Box
            width="9.97px"
            height="17.32px"
            cursor="pointer"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigateMonth('prev');
            }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            _hover={{
              opacity: 0.7
            }}
          >
            <svg width="10" height="17" viewBox="0 0 10 17" fill="none">
              <path d="M8.5 1L1.5 8.5L8.5 16" stroke="#C6D959" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Box>
          <Box
            width="9.97px"
            height="17.32px"
            cursor="pointer"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigateMonth('next');
            }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            _hover={{
              opacity: 0.7
            }}
          >
            <svg width="10" height="17" viewBox="0 0 10 17" fill="none">
              <path d="M1.5 1L8.5 8.5L1.5 16" stroke="#C6D959" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Box>
        </HStack>
      </HStack>
      
      {/* Day Headers */}
      <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap="0" marginBottom="10px" width="311px">
        {dayNames.map((day) => (
          <Box key={day} width="32px" height="18px" display="flex" alignItems="center" justifyContent="center" justifySelf="center">
            <Text
              fontFamily="Poppins, sans-serif"
              fontWeight="400"
              fontSize="14px"
              lineHeight="1.14em"
              color="rgba(235, 235, 245, 0.3)"
              textAlign="center"
            >
              {day}
            </Text>
          </Box>
        ))}
      </Box>
      
      {/* Calendar Grid */}
      <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap="0" width="311px">
        {days.map((day, index) => (
          <Box
            key={index}
            width="32px"
            height="32px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor={day ? (isValidDate(day) ? "pointer" : "not-allowed") : "default"}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              day && handleDateClick(day);
            }}
            backgroundColor={day && isSelectedDate(day) ? "#C6D959" : "transparent"}
            borderRadius={day && isSelectedDate(day) ? "100px" : "0"}
            justifySelf="center"
            marginBottom={Math.floor(index / 7) < Math.floor((days.length - 1) / 7) ? "10px" : "0"}
            opacity={day && !isValidDate(day) ? 0.3 : 1}
          >
            {day && (
              <Text
                fontFamily="Poppins, sans-serif"
                fontWeight="400"
                fontSize="20px"
                lineHeight="1em"
                color={isSelectedDate(day) ? "#120F1B" : isValidDate(day) ? "#FFFFFF" : "rgba(255, 255, 255, 0.3)"}
                textAlign="center"
              >
                {day.getDate()}
              </Text>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default Calendar;
